import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart } from 'lucide-react';
import { joyMomentImages } from '../mock';
import { JoySticker, JoyUnderline, JoyDotsField } from './JoyAccents';
import { useI18n } from '../contexts/I18nContext';

export default function CommunityInMotion() {
  const { t } = useI18n();
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setRevealed(true);
      },
      { threshold: 0.15 }
    );
    const el = document.getElementById('community-in-motion');
    if (el) obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      id="community-in-motion"
      className="relative py-20 lg:py-28 bg-[#FAF3EE] overflow-hidden"
      data-testid="community-in-motion"
    >
      <JoyDotsField className="opacity-[0.18]" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* Manifesto side */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-2 text-[#E5614C] font-semibold uppercase tracking-[0.18em] text-[12px]">
              <span className="h-[2px] w-8 bg-[#E5614C]" /> {t('cim.eyebrow')}
            </span>
            <JoySticker rotate={-5} color="yellow">{t('cim.sticker')}</JoySticker>
          </div>

          <h2 className="font-serif-display mt-5 text-[40px] sm:text-[48px] lg:text-[60px] leading-[1.02] font-bold text-[#2A1B1A]">
            {t('cim.title1')}{' '}
            <span className="block">
              {t('cim.title2')}{' '}
              <JoyUnderline color="#5DCAB5">{t('cim.title3')}</JoyUnderline>{' '}
              {t('cim.title4')}
            </span>
          </h2>

          <p className="mt-6 text-[17px] text-[#2A1B1A]/70 leading-relaxed max-w-md">
            {t('cim.desc1')} <span className="font-semibold text-[#2A1B1A]">{t('cim.desc2')}</span>
            {t('cim.desc3')}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/get-involved"
              className="inline-flex items-center gap-2 rounded-full bg-[#2A1B1A] hover:bg-[#1f1414] text-white font-semibold h-12 px-6 shadow-[0_14px_30px_-12px_rgba(42,27,26,0.55)] btn-press transition-colors"
              data-testid="cim-join-cta"
            >
              {t('cim.cta1')} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/donate"
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#E5614C] text-[#E5614C] hover:bg-[#E5614C] hover:text-white font-semibold h-12 px-6 btn-press transition-colors"
              data-testid="cim-support-cta"
            >
              <Heart className="h-4 w-4" /> {t('cim.cta2')}
            </Link>
          </div>
        </div>

        {/* Photo wall */}
        <div className="lg:col-span-7 relative">
          <div className="grid grid-cols-6 gap-3 sm:gap-4">
            {[
              { src: joyMomentImages[0], span: 'col-span-3 row-span-2 h-[260px] sm:h-[360px]', delay: 0, rotate: '-2deg' },
              { src: joyMomentImages[1], span: 'col-span-3 h-[125px] sm:h-[170px]', delay: 80, rotate: '1.5deg' },
              { src: joyMomentImages[2], span: 'col-span-3 h-[125px] sm:h-[180px]', delay: 160, rotate: '-1deg' },
              { src: joyMomentImages[3], span: 'col-span-2 h-[150px] sm:h-[200px]', delay: 240, rotate: '2deg' },
              { src: joyMomentImages[4], span: 'col-span-2 h-[150px] sm:h-[200px]', delay: 320, rotate: '-1.5deg' },
              { src: joyMomentImages[5], span: 'col-span-2 h-[150px] sm:h-[200px]', delay: 400, rotate: '1deg' },
            ].map((it, i) => (
              <div
                key={i}
                className={`relative ${it.span} rounded-2xl overflow-hidden shadow-[0_20px_40px_-20px_rgba(42,27,26,0.4)] transition-[transform,opacity] duration-700 hover:scale-[1.02] hover:rotate-0`}
                style={{
                  transitionDelay: `${it.delay}ms`,
                  transform: revealed
                    ? `translateY(0) rotate(${it.rotate})`
                    : 'translateY(24px) rotate(0deg)',
                  opacity: revealed ? 1 : 0,
                }}
              >
                <img
                  src={it.src}
                  alt={`GEMS @ The Arts community in motion — moment ${i + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>

          {/* Floating sticker accent */}
          <div className="absolute -top-4 -right-2 sm:right-4 z-10 hidden sm:block">
            <JoySticker rotate={8} color="coral">{t('cim.sticker2')}</JoySticker>
          </div>
        </div>
      </div>
    </section>
  );
}
